// import { RoutesProvider } from '@shared/core';
import React, { Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';
import { LayoutAloha } from '../../packages/layout-engine/layouts/LayoutAloha';
import { LayoutBrannan } from '../../packages/layout-engine/layouts/LayoutBrannan/LayoutBrannan';
import { useGuestSiteController } from './GuestSite.controller';
import { AnalyticsContext, useLocation } from '@shared/core';
import { PreviewListener } from './PreviewListener';
import { BrannanNavigation, DesignLayoutType } from '@graphql/generated';
import { ScrollToProvider } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/providers/ScrollToProvider/ScrollToProvider';
import { GuestSiteEvent } from '@apps/guest/packages/layout-engine/layouts/layout.types';
import { StaticContext } from '@react-router';
import { Box, JoyKitThemeProvider, useDisclosure } from '@withjoy/joykit';
import JustFonts from '@apps/guest/packages/layout-engine/components/JustFonts';
import { GuestSiteHead } from './GuestSiteHead';
import { GuestSiteDebugger } from '@apps/guest/components/GuestSiteDebugger';
import { ActionBar } from '@apps/guest/packages/layout-engine/components/ActionBar';
import { UnlockDialogProvider } from '@apps/guest/packages/layout-engine/components/UnlockDialog/UnlockDialogProvider';
import { LayoutProvider } from '@apps/guest/packages/layout-engine/layouts/LayoutProvider';
import { Announcement } from '@apps/guest/packages/layout-engine/components/Announcement';
import { BannerPublishReminder } from '@apps/guest/packages/layout-engine/components/BannerPublishReminder';
import { UnpublishedWebsiteBanner } from '@apps/guest/packages/layout-engine/components/UnpublishedWebsiteBanner';
import { wrapWithGuestSiteHealthProvider } from './GuestSite.health';
import { GuestSiteFeatureSet } from '@apps/guest/packages/layout-engine/components/GuestSiteFeatureSet/GuestSiteFeatureSet';
import { useNativeState } from '@shared/core/native';
import { useInformParentOnMousedown } from './hooks/useInformParentOnMousedown';
import { FontPackProvider } from '@apps/guest/packages/layout-engine/layouts/FontPackProvider/FontPackProvider';
import { StatusBanner } from './components/StatusBanner/StatusBanner';
import { ColorPaletteProvider } from '@apps/guest/packages/layout-engine/layouts/ColorPaletteProvider/ColorPaletteProvider';
import { SmartAppBanner, SmartAppBannerProvider, useSmartAppBannerProvider } from '@shared/components';
import { usePurchaseContextIdFromLocation } from '@apps/registry/common/utils/dropship';
import { PurchaseConfirmationDialog } from '@apps/guest/packages/layout-engine/widgets/Custom/components/TravelMap/components/PurchaseConfirmationDialog/PurchaseConfirmationDialog';
import { useUpdateSearchParam } from '@shared/utils/updateSearchParam';
import { GuestGuide } from './components/GuestGuide';
import { useEventPhoto } from '@shared/utils/eventPhoto';
import { LayoutBrannanSinglePage } from '@apps/guest/packages/layout-engine/layouts/LayoutBrannan/LayoutBrannanSinglePage';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { DiscountsProvider } from '@shared/components/DiscountsProvider/DiscountsProvider';
import { HotelsDiscountsProvider } from '@shared/components/DiscountsProvider/hotels/HotelsDiscountsProvider';
import { UnpublishedWebsite } from './components/UnpublishedWebsite/UnpublishedWebsite';
import { StickyNavBarOffsetProvider } from '@apps/guest/packages/layout-engine/layouts/LayoutBrannan/components/StickyNavBar/StickyNavBarOffset.context';

export interface GuestSiteLayoutProps
  extends Readonly<{
    eventHandle: string;
    event: GuestSiteEvent;
    layout: DesignLayoutType;
    setLoadingTheme?: Maybe<Dispatch<SetStateAction<boolean>>>;
    setIsLastPageVisible?: (value: boolean) => void;
    isNativeAccommodationPage: boolean;
  }> {}

const GuestSiteLayout: React.FC<GuestSiteLayoutProps> = ({ eventHandle, event, layout, setLoadingTheme, setIsLastPageVisible, isNativeAccommodationPage }) => {
  const brannanNavigation = event.eventDesign?.brannanNavigation;
  const [isSimpleScrollLayoutEnabled] = useIsFeatureEnabled('websiteSimpleScrollLayoutEnabled');

  const layoutSwitch = () => {
    const layoutTypes = {
      [DesignLayoutType.aloha]: LayoutAloha,
      [DesignLayoutType.brannan]:
        !isNativeAccommodationPage && brannanNavigation === BrannanNavigation.singlePage && isSimpleScrollLayoutEnabled ? LayoutBrannanSinglePage : LayoutBrannan,
    };
    const Layout = layoutTypes[layout];

    return (
      (event && (
        <Layout
          setLoadingTheme={setLoadingTheme}
          setIsLastPageVisible={setIsLastPageVisible}
          eventHandle={eventHandle}
          event={event}
        />
      )) ||
      null
    );
  };

  return layoutSwitch();
};

export interface GuestSiteProps
  extends Readonly<{
    eventHandle: string;
    staticContext: StaticContext | undefined;
  }> {}

const GuestSiteBase: React.FC<GuestSiteProps> = wrapWithGuestSiteHealthProvider(({ eventHandle, staticContext }) => {
  const {
    event,
    layout,
    eventPageRoutes,
    isPreviewing,
    handleOnPreviewUpdate,
    handleOnRefetchData,
    theme,
    loading,
    justFonts,
    graphicAccent,
    shouldRenderActionBar,
    setLoadingTheme,
    setIsLastPageVisible,
    showBannerReminderChanges,
    onDismissReminder,
    onClickReminder,
    showAnnouncement,
    onAnnouncementDismiss,
    isNativeAccommodationPage,
    showUnpublishedPage,
    isLoadingWebsitePublishCapability,
    showUnpublishedBanner,
    handlePublishWebsite,
    isPublishingWebsite,
  } = useGuestSiteController({
    eventHandle,
    staticContext,
  });
  const { isNative } = useNativeState();

  const { smartAppBannerHeight } = useSmartAppBannerProvider();

  const { pathname } = useLocation();
  const purchaseId = usePurchaseContextIdFromLocation();
  const updateSearchParam = useUpdateSearchParam();
  const analytics = useContext(AnalyticsContext);
  const [isPurchaseConfirmationOpen, setIsPurchaseConfirmationOpen] = useState(false);
  const { isOpen: isPurchaseConfirmationDialogOpen, onClose: onPurchaseConfirmationClose } = useDisclosure({
    isOpen: isPurchaseConfirmationOpen,
    onClose: () => {
      updateSearchParam('pcId', null);
      setIsPurchaseConfirmationOpen(false);
    },
  });
  const { eventPhoto } = useEventPhoto(eventHandle);

  const isTravelOrAccomPage = useMemo(() => pathname.includes('/travel') || pathname.includes('/accommodation'), [pathname]);

  useEffect(() => {
    if (purchaseId && isTravelOrAccomPage) {
      setIsPurchaseConfirmationOpen(true);
      analytics.track({
        category: 'guest.purchase',
        action: 'PageViewed',
        extraInfo: { name: 'registryPurchaseConfirmation', pageType: pathname.includes('/travel') ? 'travel' : 'accommodation' },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseId, isTravelOrAccomPage]);

  // hook to inform irame parent on mousedown
  useInformParentOnMousedown(isPreviewing);

  return (
    <>
      <Box {...(smartAppBannerHeight ? { paddingTop: smartAppBannerHeight } : {})}>
        {event && (
          <GuestSiteHead
            hideFromSearchEngines={event.settings.hideFromSearchEngines}
            eventDisplayName={event.info?.eventDisplayName}
            imageUrl={eventPhoto?.url}
            handle={event.website}
            ownerFirstName={event.info.ownerFirstName}
            ownerLastName={event.info.ownerLastName}
            fianceeFirstName={event.info.fianceeFirstName}
            pageData={event.pages}
            eventType={event.info.eventType}
          />
        )}
        {!loading && <StatusBanner />}
        <GuestSiteFeatureSet loadingGuestSite={loading || isLoadingWebsitePublishCapability}>
          {event && showUnpublishedPage ? (
            <UnpublishedWebsite />
          ) : event && layout ? (
            <LayoutProvider
              layout={layout}
              brannanNavigation={event.eventDesign?.brannanNavigation}
            >
              <FontPackProvider eventDesign={event.eventDesign}>
                <UnlockDialogProvider>
                  <ColorPaletteProvider eventDesign={event.eventDesign}>
                    <JoyKitThemeProvider theme={theme}>
                      {showAnnouncement && !isNative && (
                        <Announcement
                          onAnnouncementClosed={() => onAnnouncementDismiss(event.info.announcementBanner.title)}
                          content={<>{event.info.announcementBanner.message}</>}
                          title={event.info.announcementBanner.title}
                          graphicAccent={graphicAccent}
                        />
                      )}
                      {justFonts ? (
                        <JustFonts />
                      ) : (
                        <ScrollToProvider>
                          {isPreviewing && (
                            <PreviewListener
                              eventPageRoutes={eventPageRoutes}
                              eventDesign={event?.eventDesign}
                              onRefetchData={handleOnRefetchData}
                              onUpdate={handleOnPreviewUpdate}
                            />
                          )}
                          <StickyNavBarOffsetProvider
                            changesReminderBanner={showBannerReminderChanges}
                            unpublishedWebsiteBanner={showUnpublishedBanner}
                          >
                            <GuestSiteLayout
                              setLoadingTheme={setLoadingTheme}
                              eventHandle={eventHandle}
                              event={event}
                              layout={layout}
                              setIsLastPageVisible={setIsLastPageVisible}
                              isNativeAccommodationPage={isNativeAccommodationPage}
                            />
                          </StickyNavBarOffsetProvider>
                        </ScrollToProvider>
                      )}
                      {showBannerReminderChanges && (
                        <BannerPublishReminder
                          handleDismiss={onDismissReminder}
                          handleClickLink={onClickReminder}
                          eventHandle={eventHandle}
                        />
                      )}
                      {showUnpublishedBanner && (
                        <UnpublishedWebsiteBanner
                          onPublish={handlePublishWebsite}
                          isPublishing={isPublishingWebsite}
                        />
                      )}
                    </JoyKitThemeProvider>
                    <GuestSiteDebugger event={event} />
                  </ColorPaletteProvider>
                  {shouldRenderActionBar && (
                    <ActionBar
                      eventId={event.id}
                      showAlways={event.eventDesign?.brannanNavigation === BrannanNavigation.singlePage}
                    />
                  )}
                </UnlockDialogProvider>
              </FontPackProvider>
            </LayoutProvider>
          ) : null}
        </GuestSiteFeatureSet>
      </Box>
      <SmartAppBanner telemetryCategory="wedding" />
      <PurchaseConfirmationDialog
        isOpen={isPurchaseConfirmationDialogOpen}
        onClose={onPurchaseConfirmationClose}
      />
      <GuestGuide />
    </>
  );
});

const GuestSite: React.FC<GuestSiteProps> = props => {
  return (
    <DiscountsProvider>
      <HotelsDiscountsProvider>
        <SmartAppBannerProvider>
          <GuestSiteBase {...props} />
        </SmartAppBannerProvider>
      </HotelsDiscountsProvider>
    </DiscountsProvider>
  );
};

GuestSite.displayName = 'GuestSite';

export { GuestSite };
